import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { finalize, take } from 'rxjs';
import {
  AiSettingsService,
  saveErrorMessage,
  CompanyUsage,
  CompanyUsageResponse,
} from 'src/app/Services/ai-settings.service';

/**
 * One company's AI Agent spend for the week, and the control to raise it.
 *
 * Its own component rather than more markup in app-view-page, which is already
 * long - and so the company view keeps working on a backend that predates the
 * quota: a 404 here is reported in place instead of taking the page with it.
 */
@Component({
  selector: 'app-ai-chat-quota',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './ai-chat-quota.component.html',
  styleUrls: ['./ai-chat-quota.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatQuotaComponent implements OnChanges {
  @Input() companyId: string;

  state: CompanyUsageResponse | null = null;
  loading = false;
  loadError: string | null = null;

  /** Whether this backend knows about quotas at all. */
  supported = true;

  raiseOpen = false;
  raiseAmount: number | null = null;
  raiseReason = '';
  raiseSaving = false;
  raiseError: string | null = null;

  constructor(
    private aiSettings: AiSettingsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(): void {
    if (this.companyId) this.load();
  }

  load(): void {
    this.loading = true;
    // A failed refresh must not leave previously loaded quota data looking current.
    this.state = null;
    this.loadError = null;
    this.cdr.markForCheck();

    this.aiSettings
      .getCompanyUsage(this.companyId)
      .pipe(
        take(1),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (state) => {
          this.state = state;
          this.supported = true;
          // Pre-filled with the budget in force, so raising means editing a
          // number rather than recalling one. Nothing is sent until save.
          this.raiseAmount = state.limit?.weeklyCostLimitUsd ?? null;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          // The portal deploys independently of the backend, so an endpoint
          // that is not there yet is a state to render, not an error to shout
          // about. Anything else is worth showing as one.
          if (err.status === 404) {
            this.supported = false;
            return;
          }
          this.loadError =
            err.status === 401 || err.status === 403
              ? 'Du saknar behörighet att läsa förbrukningen. Logga in igen.'
              : 'Förbrukningen kunde inte hämtas. Försök igen om en stund.';
        },
      });
  }

  openRaise(): void {
    this.raiseOpen = true;
    this.raiseError = null;
    this.cdr.markForCheck();
  }

  cancelRaise(): void {
    this.raiseOpen = false;
    this.raiseReason = '';
    this.raiseAmount = this.state?.limit?.weeklyCostLimitUsd ?? null;
    this.raiseError = null;
    this.cdr.markForCheck();
  }

  saveRaise(): void {
    const amount = Number(this.raiseAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      this.raiseError = 'Ange ett belopp större än 0.';
      this.cdr.markForCheck();
      return;
    }
    // Checked here as well as on the server. The server is the rule; this is
    // so the person finds out before the round trip, next to the field.
    if (!this.raiseReason.trim()) {
      this.raiseError = 'Skriv varför gränsen höjs.';
      this.cdr.markForCheck();
      return;
    }
    const currentLimit = this.state?.limit?.weeklyCostLimitUsd;
    if (currentLimit !== undefined && amount <= currentLimit) {
      this.raiseError = `Den nya gränsen måste vara högre än nuvarande gräns (${this.money(
        currentLimit,
      )}).`;
      this.cdr.markForCheck();
      return;
    }

    this.raiseSaving = true;
    this.raiseError = null;
    this.cdr.markForCheck();

    this.aiSettings
      .saveCompanyLimit(this.companyId, {
        weeklyCostLimitUsd: amount,
        reason: this.raiseReason.trim(),
        // validUntil is deliberately not sent. Omitting it lets the server
        // apply its default - the end of the current week - while sending null
        // would mean "until someone says otherwise", and a raise nobody has to
        // revisit is a raise nobody revisits.
      })
      .pipe(
        take(1),
        finalize(() => {
          this.raiseSaving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.raiseOpen = false;
          this.raiseReason = '';
          // Reloaded rather than patched locally: the effective limit is
          // resolved server-side from two tables and a validity window, and
          // guessing the result here is how the two drift apart.
          this.load();
        },
        error: (err) => {
          this.raiseError = saveErrorMessage(
            err,
            'Gränsen kunde inte sparas. Försök igen.',
          );
        },
      });
  }

  /** Dollars, at the precision a weekly budget is actually read at. */
  money(usd: number | null | undefined): string {
    return `$${(usd ?? 0).toFixed(2)}`;
  }

  tokens(value: number | undefined): string {
    return (value ?? 0).toLocaleString('sv-SE');
  }

  /**
   * How much of the budget is gone, for the bar. Clamped at 100 so a company
   * that went slightly over - the last turn is priced after it is allowed -
   * does not render a bar wider than its track.
   */
  get spentPercent(): number {
    const limit = this.state?.limit?.weeklyCostLimitUsd;
    if (!limit) return 0;
    return Math.min(
      100,
      Math.round(((this.state?.usage.costUsd ?? 0) / limit) * 100),
    );
  }

  /**
   * The last day a limit actually applies, for display.
   *
   * validUntil is an exclusive instant - Monday 00:00 - so printing it raw says
   * "to 21 Sep" for a raise that in fact stops covering you at the end of the
   * 20th. One millisecond earlier lands on the day a person means, and matches
   * the form's own promise of "to the end of Sunday".
   */
  lastDayOf(validUntil: string | null | undefined): Date | null {
    if (!validUntil) return null;
    const end = new Date(validUntil);
    return Number.isNaN(end.getTime()) ? null : new Date(end.getTime() - 1);
  }

  get previousUsage(): CompanyUsage | undefined {
    return this.state?.previousWeek?.usage;
  }

  /**
   * Cache hit rate for the week, or null when the provider reported nothing.
   *
   * Worth a line of its own: it is the difference between a company that costs
   * $2 and one that costs $0.40 for the same number of messages, and it is the
   * first thing to look at when a spend figure looks wrong.
   */
  get cacheHitPercent(): number | null {
    const usage = this.state?.usage;
    if (!usage || !usage.promptTokens) return null;
    if (!usage.cachedPromptTokens) return 0;
    return Math.round((usage.cachedPromptTokens / usage.promptTokens) * 100);
  }
}
